import { useEffect, useRef, useImperativeHandle, type RefObject } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Slice } from "@tiptap/pm/model";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown as MarkdownExtension } from "@tiptap/markdown";
import { TableKit } from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { markdownDocument, MarkdownImageNote, safeMarkdownUrl } from "./markdown-document";

const components: Components = {
  img: ({ alt }) => <span className="markdown-image-note">[图片：{alt || "未命名"}]</span>,
  a: ({ href, children }) => href ? <a href={href} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{children}</a> : <span>{children}</span>,
  table: ({ children }) => <div className="markdown-table-scroll"><table>{children}</table></div>,
};
export function MarkdownPreview({ value }: { value: string }) {
  return <div className="markdown-content">{value.trim()
    ? <Markdown remarkPlugins={[remarkGfm]} skipHtml urlTransform={safeMarkdownUrl} components={components}>{value}</Markdown>
    : <p className="markdown-empty">暂无内容</p>}</div>;
}

export interface MarkdownInputHandle { focus(): void; }
interface MarkdownInputProps {
  value: string; onChange: (value: string) => void; label: string; name?: string;
  placeholder?: string; required?: boolean; invalid?: boolean; describedBy?: string;
  inputRef?: RefObject<MarkdownInputHandle | null>; className?: string;
}
export function MarkdownInput({
  value, onChange, label, name = "objective", placeholder, required = false,
  invalid = false, describedBy, inputRef, className = "",
}: MarkdownInputProps) {
  const lastValue = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ underline: false, link: {
        openOnClick: false, autolink: false, linkOnPaste: false,
        isAllowedUri: (url) => Boolean(safeMarkdownUrl(url)),
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank", referrerpolicy: "no-referrer" },
      } }),
      MarkdownExtension, MarkdownImageNote,
      TableKit.configure({ table: { resizable: false, renderWrapper: true } }),
      TaskList, TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: placeholder ?? "写下此刻的灵感…" }),
    ],
    content: markdownDocument(value),
    editorProps: {
      attributes: { role: "textbox", "aria-label": label, "aria-multiline": "true", "aria-required": String(required),
        "data-field": name, class: "markdown-content markdown-editor" },
      handlePaste(view, event) {
        const source = event.clipboardData?.getData("text/plain");
        if (!source) { event.preventDefault(); return true; }
        // Browser HTML is never parsed. Markdown paste remains one undo transaction.
        event.preventDefault();
        const json = markdownDocument(source);
        const fragment = view.state.schema.nodeFromJSON(json);
        const all = view.state.doc.textContent.length === 0 || (view.state.selection.from <= 1 && view.state.selection.to >= view.state.doc.content.size - 1);
        const open = fragment.childCount === 1 && fragment.firstChild?.type.name === "paragraph" ? 1 : 0;
        const transaction = view.state.tr.replaceSelection(new Slice(fragment.content, open, open));
        view.dispatch(transaction.scrollIntoView());
        if (all) { lastValue.current = source; onChangeRef.current(source); }
        return true;
      },
      handleDrop: (_view, event) => { event.preventDefault(); return true; },
    },
    onUpdate: ({ editor: current }) => {
      const markdown = current.isEmpty ? "" : current.getMarkdown();
      lastValue.current = markdown;
      onChangeRef.current(markdown);
    },
  });
  useImperativeHandle(inputRef, () => ({ focus: () => { editor?.view.focus(); } }), [editor]);
  useEffect(() => {
    if (!editor || value === lastValue.current) return;
    lastValue.current = value;
    editor.commands.setContent(markdownDocument(value), { emitUpdate: false });
  }, [editor, value]);
  useEffect(() => {
    if (!editor) return;
    editor.setOptions({ editorProps: { ...editor.options.editorProps, attributes: {
      ...editor.options.editorProps.attributes, "aria-invalid": String(invalid), ...(describedBy ? { "aria-describedby": describedBy } : {}),
    } } });
    if (invalid) editor.view.focus();
  }, [editor, invalid, describedBy]);
  const format = (action: "bold" | "heading" | "list" | "undo" | "redo") => {
    if (!editor) return;
    const chain = editor.chain().focus();
    if (action === "bold") chain.toggleBold().run();
    else if (action === "heading") chain.toggleHeading({ level: 2 }).run();
    else if (action === "list") chain.toggleBulletList().run();
    else if (action === "undo") chain.undo().run();
    else chain.redo().run();
  };
  return <div className={"markdown-input " + className}>
    <div className="markdown-toolbar" role="toolbar" aria-label={label + "格式"}>
      {([{ action: "heading", title: "标题", glyph: "H₂" }, { action: "bold", title: "粗体", glyph: "B" },
        { action: "list", title: "列表", glyph: "☷" }, { action: "undo", title: "撤销", glyph: "↶" },
        { action: "redo", title: "重做", glyph: "↷" }] as const).map(({ action, title, glyph }) =>
        <button type="button" key={action} title={title} aria-label={title}
          onMouseDown={(event) => event.preventDefault()} onClick={() => format(action)} disabled={!editor}>{glyph}</button>)}
    </div>
    <EditorContent editor={editor} />
    {/* A native validation bridge; the raw source is untouched until an edit. */}
    <textarea className="markdown-source" name={name} value={value} required={required} onChange={() => {}} tabIndex={-1} aria-hidden="true"
      onInvalid={(event) => { event.preventDefault(); editor?.view.focus(); }} />
  </div>;
}
